import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { Drawer, DrawerContent, DrawerClose } from './ui/drawer';
import { CrossSellProduct } from './CrossSellProduct';
import { ProductDetailModal } from './ProductDetailModal';
import { ChevronLeft, ChevronRight, Receipt, Minus, Plus } from 'lucide-react';
import { getMaxPerItem } from '../hooks/useItems.js';
import { hasProductModifiers } from '../utils/modifierHelpers.js';
import Viewport from '../utils/Viewport.js';
import { getSuggestionUnitPrice } from '../utils/suggestions.js';

export function CrossSell({currency, locale, storeId, open, cartItems, filteredStoreIds, validProducts, sellers, sellerColors, getValidProductById, getProductById, getProductModifiers, getCartProducts, addToCart, goToCart, drawerContainer, onClose, logActivity}) {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [detailProduct, setDetailProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [additionalProducts, setAdditionalProducts] = useState([]);
  const [isFullHeight, setIsFullHeight] = useState(false);

  useEffect(() => {
    const drawerContent = document.getElementById('drawer-content');
    if (drawerContent) {
      const drawerHeight = drawerContent.getBoundingClientRect().height;
      const viewportHeight = Viewport.dimensions.height;
      setIsFullHeight(drawerHeight >= viewportHeight);
    }
  }, [selectedProduct, additionalProducts]);

  const getCartItemsQuantity = () => {
    let quantity = 0;
    cartItems.forEach(item => {
      quantity += item.quantity;
    });
    return quantity;
  };

  const getCrossSellGroups = () => {
    const crossSellGroups = [];
    const cartProducts = getCartProducts();
    cartProducts.forEach(cartProduct => {
      const crossSells = cartProduct.cross_sell || [];
      crossSells.forEach(crossSellItem => {
        const crossSellId = crossSellItem.product_id;
        const productMatch = getValidProductById(crossSellId);
        if (productMatch) {
          const crossSellTitle = crossSellItem.title || '__Default__';
          const titleMatchIndex = crossSellGroups.findIndex(p => p.title === crossSellTitle);
          if (titleMatchIndex === -1) crossSellGroups.push({title: crossSellTitle, products: [{product: productMatch, crossSell: crossSellItem}]});
          else {
            const alreadyAdded = crossSellGroups[titleMatchIndex].products.find(p => p.product.id === productMatch.id);
            if (!alreadyAdded) crossSellGroups[titleMatchIndex].products.push({product: productMatch, crossSell: crossSellItem});
          }
        }
      });
    });
    return crossSellGroups;
  };

  const handleCrossSellProductClick = (product) => {
    logActivity('cross-sell', 'cross-sell-product', 'click', true, {product_id: product.id, product_name: product.label || product.name || null});
    // Los productos con modificadores necesitan que se elijan los extras antes de agregarse
    if (hasProductModifiers(product, getProductModifiers)) {
      setSelectedProduct(null);
      setQuantity(1);
      setDetailProduct(product);
      return;
    }
    setSelectedProduct(product);
  };

  const handleDetailUpsellClick = (productId) => {
    const upsellProduct = getValidProductById(productId);
    if (upsellProduct) setDetailProduct(upsellProduct);
  };

  const handleDetailAddToCart = (product, detailQuantity, selectedExtras) => {
    // Aceptación de un cross-sell con modificadores; `unit_price` es el precio base, sin extras
    logActivity('cross-sell', 'add-to-cart-button', 'click', true, {product_id: product.id, product_name: product.label || product.name || null, quantity: detailQuantity, unit_price: getSuggestionUnitPrice(product, filteredStoreIds), via: 'product-details'});
    setAdditionalProducts(prev => (prev.find(p => p.id === product.id) ? prev : [...prev, product]));
    if (addToCart) addToCart(product, detailQuantity, selectedExtras);
    setDetailProduct(null);
  };

  const handleQuantityIncrease = () => {
    setQuantity((prev) => {
      const maxPerItem = getMaxPerItem(selectedProduct, storeId, filteredStoreIds);
      let newQuantity = prev;
      if (prev >= maxPerItem) newQuantity = maxPerItem;
      else newQuantity = prev + 1;
      logActivity('cross-sell', 'increase-quantity-button', 'click', true, {product_id: selectedProduct.id, product_name: selectedProduct.label || selectedProduct.name || null, old_quantity: prev, new_quantity: newQuantity});
      return newQuantity;
    });
  };

  const handleQuantityDecrease = () => {
    setQuantity((prev) => {
      let newQuantity = prev;
      if (prev <= 0) newQuantity = 1;
      else newQuantity = prev - 1;
      logActivity('cross-sell', 'decrease-quantity-button', 'click', true, {product_id: selectedProduct.id, product_name: selectedProduct.label || selectedProduct.name || null, old_quantity: prev, new_quantity: newQuantity});
      return newQuantity;
    });
  };

  const handleClose = (silent = false) => {
    if (silent === true) logActivity('cross-sell', 'modal', 'close', false, {reason: 'go-to-cart'});
    else if (!silent) logActivity('cross-sell', 'close-button', 'close', true, null);
    setSelectedProduct(null);
    setDetailProduct(null);
    setQuantity(1);
    setAdditionalProducts([]);
    if (onClose) onClose();
  };

  const handleCancel = () => {
    if (selectedProduct) {
      logActivity('cross-sell', 'cancel-product', 'click', true, {product_id: selectedProduct.id, product_name: selectedProduct.label || selectedProduct.name || null});
      const newAdditionalProducts = [...additionalProducts];
      const index = newAdditionalProducts.findIndex(p => p.id === selectedProduct.id);
      if (index !== -1) newAdditionalProducts.splice(index, 1);
      setAdditionalProducts(newAdditionalProducts);
      setSelectedProduct(null);
      setQuantity(1);
    }
  };

  const handleAddToCart = () => {
    if (selectedProduct) {
      logActivity('cross-sell', 'add-to-cart-button', 'click', true, {product_id: selectedProduct.id, product_name: selectedProduct.label || selectedProduct.name || null, quantity: quantity, unit_price: getSuggestionUnitPrice(selectedProduct, filteredStoreIds)});
      const newAdditionalProducts = [...additionalProducts];
      const exists = newAdditionalProducts.find(p => p.id === selectedProduct.id);
      if (!exists) newAdditionalProducts.push(selectedProduct);
      setAdditionalProducts(newAdditionalProducts);
      if (addToCart) addToCart(selectedProduct, quantity, {});
      setSelectedProduct(null);
      setQuantity(1);
    }
  };

  const handleGoToCart = () => {
    logActivity('cross-sell', 'cart-button', 'click', true, null);
    handleClose(true);
    goToCart();
  };

  const renderCrossSellGroups = () => {
    const groups = getCrossSellGroups();
    return groups.map((group, index) => (
      <div key={index} className="w-full h-auto">
        <h2 className="text-4xl secondary-font-bold px-15 py-5" style={{color: `var(--color-foreground)`}}>{group.title === '__Default__' ? 'Productos destacados' : group.title}</h2>
        <div className="flex flex-wrap justify-between gap-4 px-15 py-5">
          {group.products.map(({product, crossSell}) => (
            <CrossSellProduct
              key={`${group.title}-${product.id}`}
              currency={currency}
              locale={locale}
              product={product}
              crossSell={crossSell}
              filteredStoreIds={filteredStoreIds}
              selected={(selectedProduct && selectedProduct.id === product.id) || (detailProduct && detailProduct.id === product.id)}
              onClick={handleCrossSellProductClick}
            />
          ))}
        </div>
      </div>
    ));     
  };

  const notNowButton = (
    <DrawerClose asChild>
      <motion.button
          onClick={handleClose}
          className="cursor-pointer flex items-center justify-center gap-2 w-[300px] h-[120px] rounded-[25px] btn btn-blank transition-colors secondary-font-bold"
          whileTap={{ scale: 0.8 }}
          whileHover={{ scale: 1.1 }}
        >
          <span className="text-bold fs-28">No, Gracias</span>
          <ChevronRight className="w-8 h-8 text-bold"/>
        </motion.button>
    </DrawerClose>
  );

  const cancelButton = (
    <motion.button
      className="cursor-pointer flex items-center justify-center gap-2 w-[200px] h-[120px] rounded-[25px] btn btn-blank transition-colors secondary-font-bold"
      whileTap={{ scale: 0.8 }}
      whileHover={{ scale: 1.1 }}
      onClick={handleCancel}
    >
      <ChevronLeft className="w-8 h-8 text-bold" />
      <span className="t fs-28">Cancelar</span>
    </motion.button>
  );

  const addToCartButton = (
    <motion.button
      onClick={handleAddToCart}
      className='cursor-pointer btn w-[650px] h-[120px] shadow-lg btn-primary'
      whileTap={{scale: 0.8}}
      whileHover={{scale: 1.03}}
      transition={{
        boxShadow: {delay: 0, duration: 2, repeat: Infinity, ease: 'linear' },
        scale: {duration: 0.2, ease: 'easeOut'}
      }}
      animate={{boxShadow: ['0px 0px 30px 0px var(--color-primary-glow)', '0px 0px 30px 15px var(--color-primary-glow)', '0px 0px 30px 0px var(--color-primary-glow)']}}
    >
      <span className="text-4xl lh-38 main-font-bold">Agregar al carro</span>
    </motion.button>
  );

  const goToCartButton = (
    <motion.button
      onClick={handleGoToCart}
      className='cursor-pointer btn w-[650px] h-[120px] shadow-lg btn-primary'
      whileTap={{scale: 0.8}}
      whileHover={{scale: 1.03}}
      transition={{
        boxShadow: {delay: 0, duration: 2, repeat: Infinity, ease: 'linear' },
        scale: {duration: 0.2, ease: 'easeOut'}
      }}
      animate={{boxShadow: ['0px 0px 30px 0px var(--color-primary-glow)', '0px 0px 30px 15px var(--color-primary-glow)', '0px 0px 30px 0px var(--color-primary-glow)']}}
    >
      <span className="fs-32 lh-38 main-font-bold">Continuar al carro</span>
    </motion.button>
  );

  return (
    <>
    <Drawer open={open} onClose={handleClose}>
      <DrawerContent className={`max-h-(--app-h)! bg-back border-none ${isFullHeight ? 'rounded-none' : 'rounded-t-l'}`} container={drawerContainer}>
        <div className="flex flex-col h-full overflow-hidden">
          <div className="w-full flex-1 overflow-y-auto">
            {renderCrossSellGroups()}
          </div>
          {selectedProduct ? (
            <div className="flex items-center justify-center gap-4 my-10">
              <motion.button
                onClick={handleQuantityDecrease}
                className="w-[94px] h-[94px] rounded-[20px] border-2 flex items-center justify-center hover:border-[#DE2D7A] transition-colors cursor-pointer"
                whileTap={{ scale: 0.9 }}
                whileHover={{ scale: 1.1 }}
                disabled={quantity <= 1}
                style={{ borderColor: 'var(--color-text-muted)' }}
              >
                <Minus className="w-10 h-10" style={{strokeWidth: 3}}/>
              </motion.button>
              <div className="w-[125px] h-[93px] bg-back rounded-[20px] flex items-center justify-center">
                <span className="text-bold fs-45 lh-25 text-detail-title">
                  {quantity.toString().padStart(2, '0')}
                </span>
              </div>
              <motion.button
                onClick={handleQuantityIncrease}
                className="w-[94px] h-[94px] rounded-[20px] border-2 flex items-center justify-center hover:border-[#DE2D7A] transition-colors cursor-pointer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                style={{ borderColor: 'var(--color-text-muted)' }}
              >
                <Plus className="w-10 h-10" style={{strokeWidth: 3}}/>
              </motion.button>
            </div>
          ) : null}
          <div className="text-3xl secondary-font-bold px-15 py-5 text-center flex items-center justify-center gap-5 my-10" style={{color: `var(--color-foreground)`}}>
            <div className="relative">
              <Receipt className="w-8 h-8 text-bold" />
              <div className="absolute top-[-10px] right-[-10px] w-6 h-6 rounded-full bg-primary flex items-center justify-center text-xs font-bold" style={{color: `var(--color-foreground)`}}>{cartItems.length}</div>
            </div>
            <span>Tienes ({getCartItemsQuantity()}) producto{getCartItemsQuantity() !== 1 ? 's' : ''} en tu carro</span>
          </div>
          <div className="bg-back px-8 py-6">
            {additionalProducts.length === 0 ? (
              <>
                {!selectedProduct ? (
                  <>
                    <div className="max-w-screen-xl mx-auto flex items-center justify-center gap-4">
                      {notNowButton}
                    </div>
                  </>
                ) : null}
                {selectedProduct ? (
                  <>
                    <div className="max-w-screen-xl mx-auto flex items-center justify-between gap-4">
                      {cancelButton}
                      {addToCartButton}
                    </div>
                  </>
                ) : null}
              </>
            ) : null}
            {additionalProducts.length > 0 ? (
              <>
                {!selectedProduct ? (
                  <>
                    <div className="max-w-screen-xl mx-auto flex items-center justify-center gap-4">
                      {goToCartButton}
                    </div>
                  </>
                ) : null}
                {selectedProduct ? (
                  <>
                    <div className="max-w-screen-xl mx-auto flex items-center justify-between gap-4">
                      {cancelButton}
                      {addToCartButton}
                    </div>
                  </>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      </DrawerContent>
    </Drawer>

    {/* Detalle de producto: se abre sobre el cross-sell cuando el producto tiene modificadores */}
    <ProductDetailModal
      currency={currency}
      locale={locale}
      storeId={storeId}
      product={detailProduct}
      validProducts={validProducts}
      modifiers={detailProduct ? getProductModifiers(detailProduct.modifiers || []) : []}
      sellers={sellers}
      sellerColors={sellerColors}
      open={!!detailProduct}
      onClose={() => setDetailProduct(null)}
      onUpsellClick={handleDetailUpsellClick}
      onAddToCart={handleDetailAddToCart}
      getProductById={getProductById}
      filteredStoreIds={filteredStoreIds}
      logActivity={logActivity}
      drawerContainer={drawerContainer}
    />
    </>
  );
}